/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * @file: TaskContext.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { SuspenseContentWrapper } from '@euler/app/components/SuspenseContentWrapper';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { ReportRef } from '@euler/app/flows/report/functions';
import { TaskManager } from '@euler/app/flows/task/functions/task-detail/TaskManager';
import type { TaskScreenType } from '@euler/app/flows/task/TaskFlow';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useTaskStateTrackingContext } from '@euler/functions/TaskStateTrackingContext';
import {
  AsyncState,
  useFetchAsyncState,
} from '@euler/functions/useFetchAsyncState';
import {
  Inventory,
  InventoryQuery,
  useInventory,
} from '@euler/functions/useInventory';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import {
  getTaskStatusFromTaskDetail,
  isTaskStatusChanged,
  StoreConfiguration,
  UserProfile,
} from '@euler/model';
import { TaskDetail } from '@euler/model/task-detail';
import { TaskSearchResultItem } from '@euler/model/TaskSearchResultItem';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation, useObservable } from '@euler/utils/hooks';
import produce from 'immer';
import {
  createContext,
  FC,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { skip, Subject } from 'rxjs';

export type TaskContextProps = {
  storeConfiguration: StoreConfiguration;
  taskNo: string;
  from?: string;
  continueTo?: TaskScreenType;
  continueParams?: Record<string, any>;
  detail: TaskDetail;
  taskManager: TaskManager;
  inventory: Inventory;
  inventoryQuery: InventoryQuery;
  profile: UserProfile;
  reportTarget: ReportRef;
  asyncChildTasks: AsyncState<TaskSearchResultItem[]>;
  fetchChildTasks: (...args: any[]) => Promise<TaskSearchResultItem[]>;
  focused$: Subject<void>;
  fetchDetail: (silent?: boolean) => Promise<void>;
};

export const getTaskResourceNamespace = (taskNo: string) => {
  return `tasks:${taskNo}`;
};

export const getTaskDetailResourceId = (taskNo: string) => {
  return `${getTaskResourceNamespace(taskNo)}:detail`;
};

export const TaskContext = createContext<TaskContextProps>(null as any);

export const useTaskContext = () => {
  return useContext(TaskContext);
};

export const TaskContextProvider: FC<{
  taskNo: string;
  continueTo?: TaskScreenType;
  from?: string;
  continueParams?: Record<string, any>;
  suspenseContentContainerStyle?: StyleProp<ViewStyle>;
  children?: ReactNode;
  suspenseFallback?: NonNullable<ReactNode> | null;
}> = memo(props => {
  const resourceId = getTaskDetailResourceId(props.taskNo);
  const { rpx } = useResponsiveContext();
  const { navBarHeight } = useSystemMetrics();
  const resetError = useCallback(() => {
    TaskManager.tryRecover(props.taskNo);
  }, [props.taskNo]);
  return (
    <SuspenseContentWrapper
      resourceId={resourceId}
      resetError={resetError}
      fallback={props.suspenseFallback}
      contentContainerStyle={[
        {
          paddingTop: navBarHeight + rpx(32),
        },
        props.suspenseContentContainerStyle,
      ]}
    >
      <Content {...props} />
    </SuspenseContentWrapper>
  );
});

const Content: FC<{
  taskNo: string;
  children?: ReactNode;
  from?: string;
  continueTo?: TaskScreenType;
  continueParams?: Record<string, any>;
}> = memo(({ taskNo, children, from, continueTo, continueParams }) => {
  const serviceFactory = useServiceFactory();
  const user = useCurrentUserDangerously();
  const storeConfiguration = useStoreConfiguration();
  const { statusChanges$ } = useTaskStateTrackingContext();

  const taskManager = useMemo(
    () => TaskManager.get(user, taskNo, serviceFactory),
    [user, taskNo, serviceFactory],
  );

  const resourceId = getTaskDetailResourceId(taskNo);

  const detail = useObservable(taskManager.observable, resourceId);
  const { taskService } = serviceFactory;

  const [asyncChildTasks, fetchChildTasks] = useFetchAsyncState(
    async () => {
      if (!detail.basicInfo.hasChildOrders) {
        return [];
      }
      return await taskService.list(
        {
          parentTaskNo: taskNo,
        },
        0,
        0,
        'createdAt',
        'asc',
      );
    },
    undefined,
    true,
  );

  const fetchDetail = useCallback(
    async (silence?: boolean) => {
      await taskManager.refresh(silence);
    },
    [taskManager],
  );

  const [inventory] = useInventory();
  const inventoryQuery = taskManager.useInventoryQuery(inventory);

  const focused$ = useCreation(() => new Subject<void>(), []);

  const reportTarget = useMemo<ReportRef>(
    () => ({
      type: 'task',
      id: taskNo,
      orderNo: detail.basicInfo.orderNo,
      reportNo: detail.basicInfo.reportNo,
      licensePlateNo: detail.basicInfo.licensePlateNo,
      vehicleName: detail.basicInfo.vehicleName,
      vehicleImageUrl: detail.basicInfo.vehicleImageUrl,
    }),
    [
      detail.basicInfo.orderNo,
      detail.basicInfo.reportNo,
      detail.basicInfo.licensePlateNo,
      detail.basicInfo.vehicleImageUrl,
      detail.basicInfo.vehicleName,
      taskNo,
    ],
  );

  const value = useMemo<TaskContextProps>(
    () => ({
      from,
      continueTo,
      continueParams,
      focused$,
      storeConfiguration,
      taskNo,
      detail,
      asyncChildTasks,
      fetchChildTasks,
      reportTarget,
      fetchDetail,
      taskManager,
      inventory,
      inventoryQuery,
      profile: user,
    }),
    [
      from,
      continueTo,
      continueParams,
      focused$,
      storeConfiguration,
      taskNo,
      asyncChildTasks,
      fetchChildTasks,
      reportTarget,
      detail,
      fetchDetail,
      taskManager,
      inventory,
      inventoryQuery,
      user,
    ],
  );

  useEffect(() => {
    void taskManager.fetchTaskExtraInfo();
  }, [taskManager]);

  useEffect(() => {
    const sub = taskManager.detail$.pipe(skip(1)).subscribe(d => {
      const currentStatusInfo = statusChanges$.value.get(taskNo);
      const statusInfo = getTaskStatusFromTaskDetail(d);
      if (
        !currentStatusInfo ||
        isTaskStatusChanged(currentStatusInfo, statusInfo)
      ) {
        const statusChanges = produce(statusChanges$.value, map => {
          map.set(taskNo, statusInfo);
        });
        statusChanges$.next(statusChanges);
      }
    });
    return () => {
      sub.unsubscribe();
      // remove the task status tracking for the current task
      const statusChanges = produce(statusChanges$.value, map => {
        map.delete(taskNo);
      });
      statusChanges$.next(statusChanges);
    };
  }, [statusChanges$, taskManager.detail$, taskNo]);

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
});
